import { gql } from '@apollo/client'

const SIGNON_ATTRIBUTES_FOR_BULK_SIGN_OFF = gql`
  fragment signOnAttributesForBulkSignOff on SignOn {
    id
    createdAt
    updatedAt
    archivedAt

    builderGenerated
    integrationGenerated
    integrationType
    manualSignedOnAt
    signedOffAt
    manualSignedOffAt

    project {
      id
      name
      status
      timezone
    }

    worker {
      id
      trade
      builderGenerated
      mobile

      subcontractors {
        id
        company {
          name
        }
      }

      user {
        firstName
        lastName
        email
      }
    }

    visitor {
      id
      mobile

      user {
        firstName
        lastName
      }
    }
  }
`

const SIGNON_ATTRIBUTES = gql`
  fragment signOnAttributes on SignOn {
    id
    createdAt
    updatedAt
    archivedAt

    metadata
    contextMetadata {
      createdFrom
      formName
      geolocationContext {
        autoSignOn
        autoSignOff
      }
    }
    builderGenerated
    integrationGenerated
    integrationType
    manualSignedOnAt
    signedOffAt
    manualSignedOffAt
    signOnHours {
      hours
      updatedAt
    }

    preStarts {
      id
      template {
        responseData
      }
    }

    project {
      id
      name
      status
      timezone
    }

    worker {
      id
      trade
      builderGenerated
      mobile

      subcontractors {
        id
        company {
          name
        }
      }

      user {
        firstName
        lastName
        email
      }

      tickets {
        id
        expiry
        files {
          file {
            url
            name
            contentType
          }
        }
        ticketTemplate {
          name
          shortName
        }
      }
    }

    visitor {
      id
      mobile

      user {
        firstName
        lastName
      }
    }
  }
`

export const GET_SIGNONS_FOR_BULK_SIGN_OFF = gql`
  query signOnsForBulkSignOff_UI(
    $filter: SignOnFilterInput
    $sort: SignOnSortInput
    $pagination: PaginationInput
  ) {
    signOns(filter: $filter, sort: $sort, pagination: $pagination) {
      ...signOnAttributesForBulkSignOff
    }
    signOnsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SIGNON_ATTRIBUTES_FOR_BULK_SIGN_OFF}
`

export const GET_SIGNONS = gql`
  query signOns_UI(
    $filter: SignOnFilterInput
    $sort: SignOnSortInput
    $pagination: PaginationInput
  ) {
    signOns(filter: $filter, sort: $sort, pagination: $pagination) {
      ...signOnAttributes
    }
    signOnsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
  ${SIGNON_ATTRIBUTES}
`

export const GET_SIGNONS_FOR_CSV = gql`
  query signOnsForCSV_UI(
    $filter: SignOnFilterInput
    $sort: SignOnSortInput
    $pagination: PaginationInput
  ) {
    signOns(filter: $filter, sort: $sort, pagination: $pagination) {
      id
      createdAt
      manualSignedOnAt
      manualSignedOffAt
      signedOffAt

      builderGenerated
      integrationGenerated
      integrationType

      signOnHours {
        hours
      }

      visitor {
        id

        user {
          firstName
          lastName
        }
      }

      worker {
        id
        mobile
        trade
        builderGenerated

        user {
          email
          firstName
          lastName
        }

        subcontractors {
          company {
            name
          }
        }
      }

      project {
        name
      }
    }
  }
`

export const GET_SIGNONS_LIST = gql`
  query signOnsList_UI(
    $filter: SignOnFilterInput
    $pagination: PaginationInput
  ) {
    signOns(filter: $filter, pagination: $pagination) {
      id
      createdAt
      updatedAt
      archivedAt

      builderGenerated
      integrationGenerated

      manualSignedOnAt
      signedOffAt
      manualSignedOffAt

      project {
        id
        name
        status
        timezone
      }

      worker {
        id
      }

      visitor {
        id
      }
    }
    signOnsAggregation(filter: $filter, pagination: $pagination) {
      totalCount
      totalPages
      offset
      limit
    }
  }
`

export const GET_PROCESSED_SIGNONS_ADMIN = gql`
  query processedSignOnsAdmin_UI($filter: SignOnFilterInput) {
    processedSignOns_admin(filter: $filter) {
      id
      workerId
      projectId
      signOnTime
      signOffTime
    }
  }
`

export const GET_PROCESSED_SIGNONS_WORKER = gql`
  query processedSignOnsWorker_UI($filter: ProcessedSignOnFilterInput) {
    processedSignOns_worker(filter: $filter) {
      id
      workerId
      projectId
      signOnTime
      signOffTime
    }
  }
`

export const GET_SIGNON = gql`
  query signOn_UI($signOnId: String!) {
    signOn(id: $signOnId) {
      ...signOnAttributes
    }
  }
  ${SIGNON_ATTRIBUTES}
`

export const CREATE_SIGNON = gql`
  mutation createSignOn_UI($input: CreateSignOnInput!) {
    createSignOn(input: $input) {
      ...signOnAttributes
    }
  }
  ${SIGNON_ATTRIBUTES}
`

export const MANUAL_SIGN_OFF = gql`
  mutation manuallySignOff_UI($input: ManuallySignOffInput!) {
    manuallySignOff(input: $input) {
      ...signOnAttributes
    }
  }
  ${SIGNON_ATTRIBUTES}
`

export const CREATE_WORKER_SIGN_ON_FROM_FORM = gql`
  mutation createWorkerSignOnFromForm_UI(
    $input: CreateWorkerSignOnFromFormInput!
  ) {
    createWorkerSignOnFromForm(input: $input) {
      id
    }
  }
`

export const CREATE_WORKER_SIGN_ON_FROM_FORM_BY_WORKER = gql`
  mutation createWorkerSignOnFromForm_worker_UI(
    $input: CreateWorkerSignOnFromForm_WorkerInput!
  ) {
    createWorkerSignOnFromForm_worker(input: $input) {
      id
    }
  }
`

export const MANUAL_SIGN_OFF_BULK = gql`
  mutation manuallySignOffBulk_UI($input: ManuallySignOffBulkInput!) {
    manuallySignOffBulk(input: $input) {
      ...signOnAttributesForBulkSignOff
    }
  }
  ${SIGNON_ATTRIBUTES_FOR_BULK_SIGN_OFF}
`

export const CREATE_WORKER_SIGNON = gql`
  mutation createWorkerSignOn_UI($input: CreateSignOnInput!) {
    createSignOn(input: $input) {
      id
    }
  }
`

export const CREATE_VISITOR_SIGNON = gql`
  mutation createVisitorSignOn_UI($input: CreateVisitorSignOnInput!) {
    createVisitorSignOn(input: $input) {
      id
    }
  }
`

export const ARCHIVE_SIGNON = gql`
  mutation archiveSignOn_UI($id: String!) {
    archiveSignOn(id: $id) {
      id
    }
  }
`

export const RESTORE_SIGNON = gql`
  mutation restoreSignOn_UI($id: String!) {
    restoreSignOn(id: $id) {
      id
    }
  }
`

export const SA_GET_SIGN_ONS_BY_PROJECT = gql`
  query saSignOnsByProject($projectId: String!) {
    signOnsByProject(projectId: $projectId) {
      id
      createdAt
      contextMetadata {
        geolocationContext {
          signOnGeolocationData {
            latitude
            longitude
            accuracy
          }
          signOffGeolocationData {
            latitude
            longitude
            accuracy
          }
        }
      }
    }
  }
`
